import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Trash2, Users, Upload, Image } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { mediaUrl, absoluteMediaUrl } from '../media';

const projectColors = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#ef4444',
  '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6'
];

function ProjectSettings({ project, onClose, onUpdate, onDelete }) {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || '',
    members: project.members.map(m => m._id),
    color: project.color
  });
  const [users, setUsers] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = async () => {
    try {
      await axios.put(`/projects/${project._id}/image`, { image: '' });
      setImagePreview(null);
      setImageFile(null);
      onUpdate(project._id, { ...formData, image: '' });
    } catch (error) {
      console.error('Failed to remove image:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onUpdate(project._id, formData);
      
      // Upload new image if provided
      if (imageFile) {
        console.log('Uploading project image:', imageFile);
        const imageData = new FormData();
        imageData.append('image', imageFile);
        const imageResponse = await axios.put(`/projects/${project._id}/image`, imageData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This will also delete all tasks in this project.`)) {
      try {
        await onDelete(project._id);
      } catch (error) {
        console.error('Failed to delete project:', error);
      }
    }
  };

  const toggleMember = (userId) => {
    setFormData({
      ...formData,
      members: formData.members.includes(userId)
        ? formData.members.filter(id => id !== userId)
        : [...formData.members, userId]
    });
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gray-900)' }}>
              Project Settings
            </h2>
            <button 
              onClick={onClose}
              className="btn btn-ghost"
              style={{ padding: '8px', width: '40px', height: '40px' }}
            >
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="error-message" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>Project name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="input-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="input-group">
              <label>Project colour</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {projectColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: 'var(--radius-md)',
                      background: color,
                      border: formData.color === color ? '3px solid var(--gray-900)' : '2px solid var(--gray-200)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-base)',
                      transform: formData.color === color ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Project Image */}
            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Image size={16} />
                Project Cover Image
              </label>
              
              {(imagePreview || project.image) ? (
                <div style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '2px solid var(--border-color)' }}>
                  <img 
                    src={imagePreview || mediaUrl(project.image)} 
                    alt="Project cover" 
                    style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '8px' }}>
                    <label className="btn btn-secondary btn-sm" style={{ background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', cursor: 'pointer' }}>
                      <Upload size={14} />
                      Change
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange} 
                        style={{ display: 'none' }} 
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleRemoveImage}
                      style={{ background: 'rgba(239,68,68,0.9)', color: 'white', border: 'none' }}
                    >
                      <X size={14} />
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label 
                  style={{ 
                    border: '2px dashed var(--border-color)', 
                    borderRadius: 'var(--radius-md)', 
                    padding: '32px', 
                    textAlign: 'center', 
                    cursor: 'pointer',
                    transition: 'all var(--transition-base)',
                    background: 'var(--bg-elevated)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                >
                  <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontWeight: 600 }}>Click to upload cover image</p>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>PNG, JPG up to 5MB</p>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageChange} 
                    style={{ display: 'none' }} 
                  />
                </label>
              )}
            </div>

            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={16} />
                Team Members
              </label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '2px solid var(--gray-200)', borderRadius: 'var(--radius-md)', padding: '8px' }}>
                {users.map(user => (
                  <label 
                    key={user._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      transition: 'background var(--transition-base)'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={formData.members.includes(user._id)}
                      onChange={() => toggleMember(user._id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <UserAvatar user={user} className="avatar" style={{ width: '32px', height: '32px', fontSize: '13px' }} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{user.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{user.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '2px solid var(--gray-200)' }}>
              <button type="button" className="btn btn-danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>
                <Trash2 size={18} />
                Delete Project
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProjectSettings;
